import { ref, computed} from "vue";
import { defineStore } from "pinia";
import { useReportStore } from "@/stores/reports";
import { useDashboardStore } from "@/stores/dashboards";
import { useAuthUserStore } from "@/stores/auth";
import { getRoles } from "@/services/RoleService";

const allNodes = [
    {
        id: "home",
        title: "Home",
        items: [
            {
                id: 'landing',
                title: "Landing",
                route: "landing",
                icon: "pi pi-home",
                items: []
            }
        ]
    },

    {
        id: 'dashboards',
        title: 'Dashboards',
        items: []
    },
    {
        id: 'reports',
        title: 'Reports',
        items: []
    },

    {
        id: 'settings',
        title: 'Settings',
        heading: true,
        allowedRoles: ['admin', 'curator', 'analyst'],
        items: [
            {
                id: 'tenants',
                title: 'Tenants',
                route: 'tenants',
                icon: 'pi pi-building',
                allowedRoles: ['admin', 'curator'],
            },
            {
                id: 'users',
                title: 'Users',
                route: 'users',
                icon: 'pi pi-users',
                allowedRoles: ['admin', 'curator', 'analyst'],
            },
            {
                id: 'reference-lists',
                title: 'Reference Lists',
                route: 'reference-lists',
                icon: 'pi pi-list',
                allowedRoles: ['admin', 'curator'],
            },
            {
                id: 'mapping-decisions',
                title: 'Mapping',
                route: 'mapping-decisions',
                icon: 'pi pi-sitemap',
                allowedRoles: ['admin'],
                badge: 'reviews',   // the waiting reviews, from the adminSummary store
            },
        ]
    },

    {
        id: 'data',
        title: 'Data',
        heading: true,
        allowedRoles: ['admin', 'curator', 'analyst'],
        items: [
            {
                id: 'data-ingestion',
                title: 'Data & Upload',
                route: 'data-ingestion',
                icon: 'pi pi-inbox',
                allowedRoles: ['admin', 'curator', 'analyst'],
            },

            // {
            //     id: 'connections',
            //     title: 'Connections',
            //     route: 'connections',
            //     icon: 'pi pi-plug',
            // },

            {
                id: 'backend-data',
                title: 'Semantic Mapping',
                icon: 'pi pi-database',
                items: [
                    {
                        id: 'standardise',
                        title: 'Standardise',
                        route: 'standardise',
                        icon: null,
                    },
                    {
                        id: 'categorise',
                        title: 'Categorise',
                        route: 'categorise',
                        icon: null,
                    },
                    {
                        id: 'classify',
                        title: 'Classify',
                        route: 'classify',
                        icon: null
                    },
                    {
                        id: 'interchange-fees',
                        title: 'Interchange Fees',
                        route: 'interchange-fees',
                        icon: null
                    },
                    {
                        id: 'scheme-fees',
                        title: 'Scheme Fees',
                        route: null,
                        icon: null
                    }
                ]
            },

            // After Semantic Mapping: the review reads standardised lines, so mapping comes first.
            {
                id: 'statement-review',
                title: 'Statement Review',
                route: 'statement-review',
                icon: 'pi pi-list-check',
            },

            {
                id: 'rates-and-fees',
                title: 'Rates & Fees',
                route: 'rates-and-fees',
                icon: 'pi pi-dollar',
            },

            {
                id: 'pipelines',
                title: 'Build analysis',
                route: 'pipelines',
                icon: 'pi pi-bolt',
            },

            {
                id: 'data-explorer',
                title: 'Data Explorer',
                route: 'data-explorer',
                icon: 'pi pi-search',
            },

        ]
    },

    {
        id: 'backend',
        title: 'Backend',
        allowedRoles: ['admin', 'curator', 'analyst'],
        items: [
            {
                id: 'backend-templates',
                title: 'Templates',
                route: 'templates',
                icon: 'icon-layout-template',
                allowedRoles: ['admin', 'curator'],
            },
            {
                id: 'backend-reports',
                title: 'Reports',
                route: 'reports',
                icon: 'icon-files',
            },
            {
                id: 'backend-dashboards',
                title: 'Dashboards',
                route: 'dashboards',
                icon: 'icon-layout-dashboard',
            },

            {
                id: 'backend-dashboard-items',
                title: 'Dashboard Items',
                route: 'dashboard-items',
                icon: 'icon-chart-column'
            },

            {
                id: 'backend-queries',
                title: 'Queries',
                route: 'queries',
                icon: 'pi pi-database',
            },

            {
                id: 'backend-variable-library',
                title: 'Variables',
                route: 'variable-library',
                icon: 'pi pi-hashtag',
            },
        ]
    },
]

export const useNavigationStore = defineStore("navigation", () => {

    const auth = useAuthUserStore();
    const reportStore = useReportStore();
    const dashboardStore = useDashboardStore();

    const roles = ref([]);
    const nodes = ref([]);
    const loading = ref(false);
    const initialized = ref(false);

    const getNodes = computed(() => nodes.value);
    const getRolesList = computed(() => roles.value);

    // Recursively filters nodes by the current user's role. A node with no allowedRoles
    // inherits the restriction from its parent. A node with allowedRoles: [] is explicitly
    // open to all roles, overriding any parent restriction. Parent/group nodes are kept
    // only if they have at least one visible child after filtering.
    function filterByRole(nodeList, role, inheritedRoles = null) {
        return nodeList
            .map(node => {
                const effectiveRoles = 'allowedRoles' in node ? node.allowedRoles : inheritedRoles;

                const filteredItems = node.items
                    ? filterByRole(node.items, role, effectiveRoles)
                    : undefined;

                return { ...node, ...(filteredItems !== undefined ? { items: filteredItems } : {}) };
            })
            .filter(node => {
                const effectiveRoles = 'allowedRoles' in node ? node.allowedRoles : inheritedRoles;
                return !effectiveRoles?.length || effectiveRoles.includes(role);
            });
    }

    // Bootstraps the navigation: fetches roles from the API, builds the role-filtered node
    // tree, then loads reports and dashboards and injects their dynamic navigation nodes.
    async function initReportNodes(){
        loading.value = true
        await setRoles();
        setNodes();

        await reportStore.setReports()
        await dashboardStore.setDashboards()

        await reportStore.createReportNavigationNodes()
        await dashboardStore.createDashboardNavigationNodes()
        loading.value = false
    }

    async function setRoles(){
        const role = auth.getUserRole;
        if (!['admin', 'curator', 'analyst'].includes(role)) return;
        roles.value = await getRoles();
    }

    function setNodes(){
        nodes.value = filterByRole(allNodes, auth.getUserRole)
    }

    function addNode(node, group){
        if(group){
            let groupNode = nodes.value.find((nd) => nd.id == group)
            if(!groupNode){
                const template = allNodes.find((nd) => nd.id == group)
                if(!template) return
                groupNode = { ...template, items: [] }
                const templateIndex = allNodes.findIndex((nd) => nd.id == group)
                let insertIndex = nodes.value.length
                for(let i = templateIndex + 1; i < allNodes.length; i++){
                    const laterIndex = nodes.value.findIndex((nd) => nd.id === allNodes[i].id)
                    if(laterIndex !== -1){ insertIndex = laterIndex; break }
                }
                nodes.value.splice(insertIndex, 0, groupNode)
            }
            const alreadyExists = nodes.value.some((nd) => nd.items?.find((n) => n.id == node.id))
            if(!alreadyExists){
                groupNode.items.push(node)
            }
        } else {
            nodes.value.push(node)
        }
    }

    function editNode(nodeId, node){
        let index = nodes.value.findIndex((node) => node.id == nodeId)

        nodes[index] = node
    }

    function removeNode(id, type = 'dashboard'){
        /*
        let index = nodes.value.findIndex((node) => node.id == id)

        nodes.value.splice(index, 1)
         }

        const index = nodes.value.findIndex(
            (node) => node.id == id && node.type == type
        )
        if(index !== -1){
            nodes.value.splice(index, 1);
    }*/

        const dashboards = nodes.value.find(n => n.id === 'dashboards');
        if (!dashboards) return;

        dashboards.items = dashboards.items.filter(n => String(n.id) !== String(id));

        nodes.value = [...nodes.value];
    }

    function $reset(){
        // Reset the stores to default
        dashboardStore.$reset()
        reportStore.$reset()

        // Reset the nodes to default
        nodes.value = []
        roles.value = []
        loading.value = false
    }


  return { nodes, roles, loading, getNodes, getRolesList, addNode, editNode, removeNode, initReportNodes, setNodes, $reset };
});
